import { useState } from 'react';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Badge, Card, Heading, Inline, LinkButton, RichTextContent, Stack, Text, vars } from '@etg/wings';
import { CaretDown } from '@phosphor-icons/react';
import { AirportAvailability, ExpandableContent } from '@eti/components';
import { TripType } from '@eti/schema-types';
import { getUpsellProps } from '../../common/utils/productCardProps';
import { getAvailability, getProductData } from '../utils/utils';
import { getProductTranslationsByProductId } from '../../common/utils/getProductTranslations';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const contentWrapperStyles = css`
  padding: 16px 0;
`;
const headingStyles = css`
  justify-content: space-between;
`;
const iconStyles = showMore => css`
  color: ${vars.colors.links.default};
  flex-shrink: 0;
  transform: rotateX(${showMore ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;
const upSellStyles = css`
  display: flex;
  width: fit-content;
`;
const EtgFaresDrawerContent = ({
  bounds,
  description,
  dimensions,
  id,
  name,
  piecesAndWeight,
  productSpecificTexts,
  productTypeId,
  readMoreText,
  termsUrl,
  tripType
}) => {
  var _upSellObject$content;
  const {
    t
  } = useTranslation();
  const readMoreButtonText = t('Products.ReadMore.Link.Label');
  const readLessButtonText = t('Products.ReadLess.Link.Label');
  const coverGeniusPolicyLinkText = t('coverGeniusPolicyLinkText');
  const openInNewTabText = t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text');
  const [accordionData, setAccordionData] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const readMoreButtonLabel = showMore ? readLessButtonText : readMoreButtonText;
  const handleClick = () => {
    setShowMore(state => !state);
  };
  const isMultiStop = tripType !== TripType.OneWay && tripType !== TripType.Return;
  const toggleAccordion = airport => {
    if (!airport || airport.boundIndex === (accordionData === null || accordionData === void 0 ? void 0 : accordionData.boundIndex) && airport.segmentIndex === (accordionData === null || accordionData === void 0 ? void 0 : accordionData.segmentIndex)) {
      setAccordionData(null);
    } else {
      setAccordionData(airport);
    }
  };
  const termsLink = /*#__PURE__*/_jsx(Inline, {
    children: /*#__PURE__*/_jsx("a", {
      "aria-label": `${coverGeniusPolicyLinkText} (${openInNewTabText})`,
      href: termsUrl !== null && termsUrl !== void 0 ? termsUrl : undefined,
      rel: "noreferrer",
      target: "_blank",
      children: coverGeniusPolicyLinkText
    })
  });
  const translations = getProductTranslationsByProductId(productTypeId, t);
  const availabilityList = productSpecificTexts ? getAvailability(bounds, productSpecificTexts, productTypeId) : [];
  const shouldDisplayAvailability = availabilityList.length > 0;
  const {
    upSellObject,
    shouldDisplayUpsell = false
  } = shouldDisplayAvailability ? getUpsellProps(translations.upSell, {
    shouldDisplayAvailability,
    availabilityList,
    tripType
  }) : {};
  const productData = getProductData(availabilityList, translations, productTypeId, shouldDisplayAvailability, shouldDisplayUpsell, upSellObject);
  return /*#__PURE__*/_jsxs(Card, {
    children: [/*#__PURE__*/_jsxs(Inline, {
      alignY: "center",
      className: headingStyles,
      children: [/*#__PURE__*/_jsx(Heading, {
        "data-testid": "content-title",
        level: 3,
        children: /*#__PURE__*/_jsxs("bdi", {
          children: [name, piecesAndWeight && ` (${piecesAndWeight})`]
        })
      }), shouldDisplayAvailability && (upSellObject === null || upSellObject === void 0 ? void 0 : upSellObject.content) && /*#__PURE__*/_jsx(Badge, {
        className: upSellStyles,
        "data-testid": "partial-availability-pill-information",
        variant: "information",
        children: upSellObject.content
      })]
    }), /*#__PURE__*/_jsx(RichTextContent, {
      className: contentWrapperStyles,
      children: description
    }), /*#__PURE__*/_jsxs(Stack, {
      children: [piecesAndWeight && /*#__PURE__*/_jsx(Text, {
        as: "bdi",
        children: piecesAndWeight
      }), dimensions && /*#__PURE__*/_jsx(Text, {
        as: "bdi",
        children: dimensions
      }), termsUrl && !readMoreText && termsLink, readMoreText && !piecesAndWeight && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Inline, {
          children: /*#__PURE__*/_jsx(LinkButton, {
            "aria-controls": `read-more-${id}`,
            "aria-expanded": showMore,
            "aria-label": `${readMoreButtonLabel} ${name} ${(_upSellObject$content = upSellObject === null || upSellObject === void 0 ? void 0 : upSellObject.content) !== null && _upSellObject$content !== void 0 ? _upSellObject$content : ''}`,
            "data-testid": "readMore-button",
            onClick: handleClick,
            children: /*#__PURE__*/_jsxs(Inline, {
              noWrap: true,
              spacing: 4,
              children: [readMoreButtonLabel, /*#__PURE__*/_jsx(CaretDown, {
                "aria-hidden": true,
                className: iconStyles(showMore),
                size: 20,
                weight: "light"
              })]
            })
          })
        }), /*#__PURE__*/_jsx(ExpandableContent, {
          isExpanded: showMore,
          children: /*#__PURE__*/_jsxs(Stack, {
            id: `read-more-${id}`,
            children: [shouldDisplayAvailability && /*#__PURE__*/_jsx(AirportAvailability, {
              accordionData: accordionData,
              isMultiStop: isMultiStop,
              productData: productData,
              testid: "etgFares-yst",
              toggleAccordion: toggleAccordion
            }), /*#__PURE__*/_jsx(RichTextContent, {
              children: readMoreText
            }), termsUrl && termsLink]
          })
        })]
      })]
    })]
  });
};
export default EtgFaresDrawerContent;
//# sourceMappingURL=EtgFaresDrawerContent.js.map